import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';

import Input from '@/shared/components/Input';
import { _t } from '@/i18n/t';
import { cn } from '@/shared/lib/utils';

interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface MultiSelectProps {
  value: string[];
  onValueChange: (value: string[]) => void;
  options: MultiSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  noResultsText: string;
  selectedCountText: (count: number) => string;
  label?: React.ReactNode;
  labelClassName?: string;
  wrapperClassName?: string;
  disabled?: boolean;
  footer?: React.ReactNode;
  'data-testid'?: string;
}

const MultiSelect = ({
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  noResultsText,
  selectedCountText,
  label,
  labelClassName,
  wrapperClassName,
  disabled,
  footer,
  'data-testid': testId,
}: MultiSelectProps) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = React.useState<React.CSSProperties>({});

  const updatePanelPosition = React.useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportPadding = 8;
    const gap = 4;
    const minHeight = 160;
    const preferredHeight = 360;

    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding - gap;
    const spaceAbove = rect.top - viewportPadding - gap;

    const openAbove = spaceBelow < minHeight && spaceAbove > spaceBelow;
    const available = openAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(minHeight, Math.min(preferredHeight, available));

    setPanelStyle({
      position: 'fixed',
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
      left: rect.left,
      width: rect.width,
      maxHeight,
      zIndex: 100,
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      const clickedTrigger = rootRef.current?.contains(target);
      const clickedPanel = panelRef.current?.contains(target);

      if (!clickedTrigger && !clickedPanel) {
        setOpen(false);
      }
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    const onViewportChange = () => updatePanelPosition();

    updatePanelPosition();
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [open, updatePanelPosition]);

  const filteredOptions = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, search]);
  const selectedOptions = React.useMemo(() => {
    const optionLabelMap = new Map(options.map((option) => [option.value, option.label]));
    return value.map((selectedValue) => ({
      value: selectedValue,
      label: optionLabelMap.get(selectedValue) ?? selectedValue,
    }));
  }, [options, value]);

  const summary = value.length > 0 ? selectedCountText(value.length) : placeholder;

  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onValueChange(value.filter((v) => v !== optionValue));
    } else {
      onValueChange([...value, optionValue]);
    }
  };

  return (
    <div ref={rootRef} className={cn('space-y-1.5', wrapperClassName)}>
      {label ? (
        <label data-th-typography="form-label" className={cn('block text-form-label text-label', labelClassName)}>
          {label}
        </label>
      ) : null}

      <button
        ref={triggerRef}
        data-th-button="true"
        data-th-typography="form-control"
        data-testid={testId}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-xl bg-white px-4 text-left text-form-control font-normal text-input-text shadow-none outline-none transition-colors',
          'disabled:cursor-not-allowed disabled:bg-[rgba(229,231,235,0.35)] disabled:!border-[rgba(229,231,235,0.6)] disabled:text-muted-foreground',
          open && 'ring-2 ring-primary/20',
        )}
        style={{ border: '1px solid #d5dde7' }}
      >
        <span className={cn(value.length === 0 && 'text-input-text/80')}>{summary}</span>
        <ChevronDown className={cn('h-4 w-4 text-[#818a99] transition-transform duration-200', open && 'rotate-180')} />
      </button>

      {selectedOptions.length > 0 ? (
        <div className="flex flex-wrap gap-2 pt-0.5">
          {selectedOptions.map((selectedOption) => (
            <div
              key={selectedOption.value}
              data-th-typography="form-option-title"
              className="inline-flex items-center gap-1.5 rounded-[6px] bg-[hsl(210,100%,96%)] px-[10px] py-[4px] text-form-option-title font-normal text-[#000]"
            >
              <span>{selectedOption.label}</span>
              <button
                type="button"
                disabled={disabled}
                aria-label={_t('aria.removeSelection', { label: selectedOption.label })}
                onClick={() => toggleOption(selectedOption.value)}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[#000] transition-colors hover:text-[#000] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            aria-multiselectable="true"
            data-th-typography="form-control"
            className="z-[100] mt-1 flex w-full flex-col overflow-hidden rounded-xl border border-[#d5dde7] bg-white shadow-[0_0_0_1px_rgba(213,221,231,0.65),0_8px_20px_rgba(15,23,42,0.08)]"
            style={panelStyle}
          >
            <div className="border-b border-[#d5dde7] px-3 py-2.5">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={searchPlaceholder}
                aria-label={_t('aria.searchOptions')}
                className="h-11 border-[#d5dde7] bg-[#f4f6f9] font-normal placeholder:text-input-text/70"
              />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">{noResultsText}</div>
              ) : (
                filteredOptions.map((option) => {
                  const checked = value.includes(option.value);
                  return (
                    <div
                      key={option.value}
                      role="option"
                      aria-selected={checked}
                      aria-disabled={option.disabled}
                      tabIndex={option.disabled ? -1 : 0}
                      onClick={() => toggleOption(option.value)}
                      onKeyDown={(event) => {
                        if (option.disabled) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          toggleOption(option.value);
                        }
                      }}
                      className={cn(
                        'flex w-full items-center gap-2.5 px-3 py-2 text-left text-form-control text-input-text outline-none hover:bg-[#eef2f6] focus:bg-[#eef2f6]',
                        option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[3px] bg-white shadow-[inset_0_0_0_1px_#d5dde7]',
                          checked && 'bg-[#1f4b8f] shadow-[inset_0_0_0_1px_#1f4b8f]',
                        )}
                      >
                        {checked ? <Check className="h-2.5 w-2.5 text-white" /> : null}
                      </span>
                      <span className="text-form-control text-input-text">{option.label}</span>
                    </div>
                  );
                })
              )}
            </div>

            {footer ? (
              <div className="flex-shrink-0" style={{ boxShadow: 'inset 0 1px 0 #d5dde7' }}>
                {footer}
              </div>
            ) : null}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default MultiSelect;
